import {
  CounterClockwiseClockIcon,
  LinkBreak2Icon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IAdminPatientDevice } from "../../../../@types/admin/AdminDevice";
import { TableSorted } from "../../../../components/TableSorted";
import { useAdminPatientDevices } from "../../../../hooks/queries/admin/devices";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  EmailLink,
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  RowActionGroup,
  SearchInput,
  StatusBadge,
} from "../AdminPage.styles";
import { RowAction } from "../RowAction";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";
import { AddDeviceModal } from "./AddDeviceModal";
import { DeviceHistoryModal } from "./DeviceHistoryModal";
import { DisconnectDeviceModal } from "./DisconnectDeviceModal";
import { EditIdentifierModal } from "./EditIdentifierModal";

const SEARCH_FIELDS: (keyof IAdminPatientDevice)[] = [
  "deviceName",
  "identifier",
  "patientName",
  "patientEmail",
  "patientId",
  "status",
];

/** Retool tagged the status with automatic colours; these are the meanings. */
function statusTone(status: string): "active" | "inactive" | "warning" {
  const normalised = status?.toLowerCase() ?? "";
  if (normalised.includes("active") || normalised.includes("connected")) {
    return "active";
  }
  if (normalised.includes("pending")) return "warning";
  return "inactive";
}

export interface IPatientDevicesPanelProps {
  /**
   * Restrict to one patient, and lock the add form to them. Omit for every
   * device — Retool's `patientId` module prop, blank on the Cellular Devices
   * page and filled in on the patient pages.
   */
  patientId?: number;
  /** `null` where the page or modal around it already names the list. */
  title?: string | null;
  /** Rows per page. Defaults to the shared /admin page size. */
  pageSize?: number;
}

/**
 * Patient devices — the portal port of Retool's `moduleDevices` (EN-5166).
 *
 * Shipped as a shared component because Retool embedded the same module in
 * the Patients, Archived Patients and Hospital Details pages; **EN-5170,
 * EN-5171 and EN-5172/EN-5175 embed this**, they do not inline another
 * devices table.
 *
 *   <PatientDevicesPanel />                        // every device
 *   <PatientDevicesPanel patientId={patient.id} /> // one patient's
 *
 * Every write is keyed by the device's `identifier`, not by an id — that is
 * the API's contract, and it is why renaming an identifier is its own action.
 */
export function PatientDevicesPanel({
  patientId,
  title = "Devices",
  pageSize,
}: IPatientDevicesPanelProps) {
  const { openModal, closeModal } = useModal();
  const { devices, isLoading, isError } = useAdminPatientDevices(patientId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: devices,
    defaultPageSize: pageSize,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<IAdminPatientDevice, string>[] = useMemo(
    () => [
      { accessorKey: "deviceName", header: "Device", enableSorting: true },
      { accessorKey: "identifier", header: "Identifier", enableSorting: true },
      { accessorKey: "patientName", header: "Patient", enableSorting: true },
      {
        accessorKey: "patientEmail",
        header: "Patient email",
        cell: ({ row }) =>
          row.original.patientEmail ? (
            <EmailLink href={`mailto:${row.original.patientEmail}`}>
              {row.original.patientEmail}
            </EmailLink>
          ) : (
            <span>-</span>
          ),
        enableSorting: true,
      },
      {
        id: "patientId",
        header: "Patient ID",
        accessorFn: (device) =>
          device.patientId == null ? "" : String(device.patientId),
        enableSorting: true,
      },
      {
        accessorKey: "lastUsed",
        header: "Last used",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastUsed, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.createdAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (device) => device.status,
        cell: ({ row }) => (
          <StatusBadge status={statusTone(row.original.status)}>
            {row.original.status}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="History"
              icon={<CounterClockwiseClockIcon />}
              onClick={() => {
                openModal(<DeviceHistoryModal device={row.original} />, {
                  width: "min(900px, 96vw)",
                  maxHeight: "85vh",
                  showCloseButton: true,
                });
              }}
            />
            <RowAction
              label="Edit identifier"
              icon={<Pencil1Icon />}
              onClick={() => {
                openModal(
                  <EditIdentifierModal
                    device={row.original}
                    onClose={closeModal}
                  />,
                  { width: "460px", showCloseButton: true }
                );
              }}
            />
            <RowAction
              label="Disconnect"
              icon={<LinkBreak2Icon />}
              danger
              onClick={() => {
                openModal(
                  <DisconnectDeviceModal
                    device={row.original}
                    onClose={closeModal}
                  />,
                  { width: "460px", showCloseButton: true }
                );
              }}
            />
          </RowActionGroup>
        ),
      },
    ],
    []
  );

  return (
    <PanelContainer>
      {title && (
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
        </PanelHeader>
      )}

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by device, identifier, patient or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <FiltersActions>
          <FilterButton
            variant="primary"
            onClick={() =>
              openModal(
                <AddDeviceModal patientId={patientId} onClose={closeModal} />,
                { width: "460px", showCloseButton: true }
              )
            }
          >
            Add device
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the devices. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-patient-devices"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "patient-devices",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PanelContainer>
  );
}
